import { Button } from "@/components/ui/button";
import { Pencil, Trash2 } from "lucide-react";

const money = (v) => (v == null || v === "" ? "—" : `₹${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`);
const Th = ({ items }) => (
  <thead><tr className="border-b border-border text-left">
    {items.filter(Boolean).map((h) => <th key={h} className="px-3 py-3 text-[10px] font-mono uppercase tracking-widest text-muted-foreground whitespace-nowrap">{h}</th>)}
  </tr></thead>);
const Empty = ({ span, children }) => <tr><td colSpan={span} className="px-4 py-8 text-center text-sm text-muted-foreground">{children}</td></tr>;
const Wrap = ({ testid, children }) => <div className="bg-card border border-border rounded-lg overflow-x-auto"><table className="w-full text-sm" data-testid={testid}>{children}</table></div>;

const specs = (p) => [p.specs?.capacity && `${p.specs.capacity}${p.specs.capacity_unit || "ml"}`, p.specs?.material, p.specs?.bottle_weight_g && `${p.specs.bottle_weight_g}g`,
  p.specs?.neck_size_mm && `${p.specs.neck_size_mm}mm`, p.specs?.shape].filter(Boolean).join(" · ") || "—";
const statusCls = (s) => ((s || "active") === "active" ? "bg-signal-green/15 text-signal-green" : s === "inactive" ? "bg-muted text-muted-foreground" : "bg-signal-red/15 text-signal-red");

export function ProductTable({ rows, canEdit, onEdit }) {
  return (
    <Wrap testid="products-table">
      <Th items={["SKU", "Product", "Category", "Specs", "Colour", "Pcs/Carton", "Std Rate", "MSP", "Floor", "Cost", "GST", "Status", " "]} />
      <tbody>
        {rows.map((p) => (
          <tr key={p.sku} className="border-b border-border/50" data-testid={`product-row-${p.sku}`}>
            <td className="px-3 py-2.5 font-mono text-xs text-primary font-bold">{p.sku}</td>
            <td className="px-3 py-2.5 font-medium">{p.name}</td>
            <td className="px-3 py-2.5 text-xs text-muted-foreground">{p.category || "—"}{p.subcategory ? ` / ${p.subcategory}` : ""}</td>
            <td className="px-3 py-2.5 text-xs text-muted-foreground whitespace-nowrap">{specs(p)}</td>
            <td className="px-3 py-2.5 text-xs">{p.specs?.colour || "—"}</td>
            <td className="px-3 py-2.5 metric text-xs">{p.pieces_per_carton || "—"}</td>
            <td className="px-3 py-2.5 metric text-xs">{money(p.standard_rate)}</td>
            <td className="px-3 py-2.5 metric text-xs text-signal-amber">{money(p.msp)}</td>
            <td className="px-3 py-2.5 metric text-xs text-signal-red">{money(p.floor_price)}</td>
            <td className="px-3 py-2.5 metric text-xs text-muted-foreground">{money(p.cost_price)}</td>
            <td className="px-3 py-2.5 metric text-xs">{p.gst_percent}%</td>
            <td className="px-3 py-2.5"><span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded ${statusCls(p.status)}`}>{p.status || "active"}</span></td>
            <td className="px-3 py-2.5">{canEdit && <Button size="sm" variant="ghost" onClick={() => onEdit(p)} data-testid={`edit-product-${p.sku}`}><Pencil className="w-3.5 h-3.5" /></Button>}</td>
          </tr>))}
        {rows.length === 0 && <Empty span={13}>No products yet — create the first SKU master.</Empty>}
      </tbody>
    </Wrap>
  );
}

export function ColourTable({ rows }) {
  return (
    <Wrap testid="colours-table">
      <Th items={["ID", "Colour", "Code", "Masterbatch Ref", "Customer Ref", "Remarks"]} />
      <tbody>
        {rows.map((c) => (
          <tr key={c.colour_id} className="border-b border-border/50" data-testid={`colour-row-${c.colour_id}`}>
            <td className="px-3 py-2.5 font-mono text-xs text-muted-foreground">{c.colour_id}</td>
            <td className="px-3 py-2.5 font-medium">{c.name}</td>
            <td className="px-3 py-2.5 font-mono text-xs">{c.code || "—"}</td>
            <td className="px-3 py-2.5 text-xs text-muted-foreground">{c.masterbatch_ref || "—"}</td>
            <td className="px-3 py-2.5 text-xs text-muted-foreground">{c.customer_ref || "—"}</td>
            <td className="px-3 py-2.5 text-xs text-muted-foreground">{c.remarks || "—"}</td>
          </tr>))}
        {rows.length === 0 && <Empty span={6}>No colours in master yet.</Empty>}
      </tbody>
    </Wrap>
  );
}

export function CustomerPriceTable({ rows, customers, canEdit, onDelete }) {
  return (
    <Wrap testid="customer-prices-table">
      <Th items={["ID", "Customer", "SKU", "Price", "Effective From", "Effective To", "Remarks", " "]} />
      <tbody>
        {rows.map((cp) => (
          <tr key={cp.cp_id} className="border-b border-border/50" data-testid={`cp-row-${cp.cp_id}`}>
            <td className="px-3 py-2.5 font-mono text-xs text-muted-foreground">{cp.cp_id}</td>
            <td className="px-3 py-2.5">{customers.find((c) => c.code === cp.customer_code)?.name || cp.customer_code}</td>
            <td className="px-3 py-2.5 font-mono text-xs text-primary">{cp.sku}</td>
            <td className="px-3 py-2.5 metric font-bold">{money(cp.price)}</td>
            <td className="px-3 py-2.5 text-xs">{cp.effective_from || "—"}</td>
            <td className="px-3 py-2.5 text-xs">{cp.effective_to || "open"}</td>
            <td className="px-3 py-2.5 text-xs text-muted-foreground">{cp.remarks || "—"}</td>
            <td className="px-3 py-2.5">{canEdit && <Button size="sm" variant="ghost" onClick={() => onDelete(cp)} data-testid={`cp-delete-${cp.cp_id}`}><Trash2 className="w-3.5 h-3.5 text-signal-red" /></Button>}</td>
          </tr>))}
        {rows.length === 0 && <Empty span={8}>No customer-specific prices. Standard rates apply to all customers.</Empty>}
      </tbody>
    </Wrap>
  );
}
